import React, { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { DataTable } from "ui-components/data-table";
import Loading from "ui-components/loading/loading";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import ScanningService from "../../../app/data/dock-scan/scanningService";
import { ScanLog } from "../../../app/data/dock-scan/models";
import ScanLogDetailSidebar from "../../dock-scan-management/components/ScanLogDetailSidebar";
import {
  DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS,
  DOCK_SCAN_LOGS_LIST_COLUMNS_HIDDEN_BY_DEFAULT,
  getDockScanLogsListColumns,
} from "../../dock-scan-management/dockScanLogsListColumns";
import "features/dock-scan-management/dockUsers.scss";

const scanningService = ScanningService.getInstance();
const PAGE_SIZE = 50;

export interface ShipmentDockScanLogNewProps {
  invoiceNumber: number;
}

const ShipmentDockScanLogNew: React.FC<ShipmentDockScanLogNewProps> = ({ invoiceNumber }) => {
  const [selectedLog, setSelectedLog] = useState<ScanLog | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const hidden = new Set(DOCK_SCAN_LOGS_LIST_COLUMNS_HIDDEN_BY_DEFAULT);
    return DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS.reduce(
      (acc, { id }) => ({ ...acc, [id]: !hidden.has(id) }),
      {} as Record<string, boolean>
    );
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS.map(({ id }) => id)
  );

  const queryParams = useMemo(
    () => ({
      probillNumber: invoiceNumber,
      take: PAGE_SIZE,
    }),
    [invoiceNumber]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.DOCK_SCAN_LOGS, "shipment", invoiceNumber, queryParams],
    queryFn: async ({ pageParam }) =>
      scanningService.listScanLogs({
        ...queryParams,
        cursor: pageParam as string | undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!invoiceNumber,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const rows = useMemo(
    () => data?.pages.flatMap((page) => page?.items ?? []) ?? [],
    [data]
  );

  const columns = useMemo(() => getDockScanLogsListColumns(), []);

  return (
    <>
      {rows.length > 0 && (
        <div className="dock-scan-log-new__count text-light">
          Showing {rows.length} scan log{rows.length === 1 ? "" : "s"}
          {hasNextPage ? " · more available" : ""}
        </div>
      )}

      {isLoading && (
        <div className="xgs-invoice-documents__loading">
          <Loading isLoading />
        </div>
      )}

      {isError && (
        <div className="xgs-shipment-details__tabs__error">
          {(error as Error)?.message || "Error while loading scan logs."}
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div className="dock-scan-log-new">
          <DataTable
            columns={columns}
            data={rows}
            rowHeight={56}
            responsive
            stickyHeaders
            enableColumnResize
            enableColumnReorder
            showColumnSelector
            columnSelectorOptions={DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            columnOrder={columnOrder}
            onColumnOrderChange={setColumnOrder}
            infiniteScroll
            infiniteScrollLoading={isFetchingNextPage}
            infiniteScrollHasNext={!!hasNextPage}
            onInfiniteScroll={() => fetchNextPage()}
            cursorPointer
            onRowClicked={(original) => setSelectedLog(original as ScanLog)}
            listHeightOverride={360}
          />
        </div>
      )}

      {!isLoading && rows.length === 0 && !isError && (
        <div className="xgs-shipment-details__tabs__no-data">
          No scan logs found for this probill.
        </div>
      )}

      <ScanLogDetailSidebar
        show={!!selectedLog}
        scanLog={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </>
  );
};

export default ShipmentDockScanLogNew;
